/**
 * Property title-reference terminology and formatting, per Australian
 * state/territory (LF-4071).
 *
 * A property's "title reference" is the short identifier that uniquely
 * identifies the folio of the register — e.g. NSW `18/218470`, VIC `8555/407`,
 * WA `210000341`. The terminology differs by jurisdiction, and so does the
 * expected shape of the reference. This module is the single source of truth
 * for:
 *
 * - the **label** shown against the field ("Title Reference" / "Vol/Folio" /
 *   "Register ID");
 * - the **placeholder / format hint** displayed in the input; and
 * - a **lenient validation pattern** used to reject obviously-wrong values
 *   (e.g. the descriptive `Lot 18/DP 218470` rather than the reference
 *   `18/218470`) without being so strict it rejects the many legitimate
 *   variants each registry accepts.
 *
 * Labels follow the product decision on LF-4071 (NSW/QLD/SA → "Title
 * Reference", VIC → "Vol/Folio", WA → "Register ID"). TAS and ACT use the
 * Volume/Folio terminology of their registries; NT mixes Lot-on-Plan and
 * Volume/Folio so it keeps the generic "Title Reference".
 *
 * The patterns are intentionally permissive: a reference is a compact token of
 * letters/digits joined by `/` or `-`, with no embedded spaces or words. That
 * is enough to catch descriptive free-text the AI sometimes proposes while
 * still accepting the full spread of real references (DP/SP/strata/auto-consol
 * for NSW, vol/folio for VIC/SA/TAS/ACT/NT, numeric title refs for QLD, and
 * register IDs for WA).
 */

import { AUSTRALIAN_STATES } from './australian-constants';

export type AuStateCode = (typeof AUSTRALIAN_STATES)[number]['value'];

export interface TitleReferenceConfig {
  /** Field label for this jurisdiction. */
  label: string;
  /** Example reference(s) for this jurisdiction — used as the format hint. */
  example: string;
  /** Lenient pattern the trimmed reference must match. */
  pattern: RegExp;
}

/** Volume/Folio or Lot/Plan — requires at least one `/` or `-` separator. */
const VOLUME_FOLIO = /^[A-Za-z0-9]+(?:[-/][A-Za-z0-9]+)+$/;

/**
 * Compact identifier — separators optional. Covers QLD numeric title
 * references (`13333016`) and WA register IDs (`210000341`, `0030800175A`,
 * `2100-341`).
 */
const COMPACT_ID = /^[A-Za-z0-9]+(?:[-/][A-Za-z0-9]+)*$/;

/**
 * SA volume/folio with an optional leading title-type code (`CT`, `CL`, `CR`),
 * e.g. `CT 5441/221` or `5441/221`.
 */
const SA_TITLE = /^(?:[A-Za-z]{2,3}\s)?[A-Za-z0-9]+(?:[-/][A-Za-z0-9]+)+$/;

export const TITLE_REFERENCE_CONFIG: Record<AuStateCode, TitleReferenceConfig> =
  {
    NSW: {
      label: 'Title Reference',
      example: '18/218470 or 18/SP28304',
      pattern: VOLUME_FOLIO
    },
    VIC: { label: 'Vol/Folio', example: '8555/407', pattern: VOLUME_FOLIO },
    QLD: { label: 'Title Reference', example: '13333016', pattern: COMPACT_ID },
    SA: { label: 'Title Reference', example: 'CT 5441/221', pattern: SA_TITLE },
    WA: { label: 'Register ID', example: '210000341', pattern: COMPACT_ID },
    TAS: { label: 'Vol/Folio', example: '156432/1', pattern: VOLUME_FOLIO },
    ACT: { label: 'Vol/Folio', example: '1234/56', pattern: VOLUME_FOLIO },
    NT: { label: 'Title Reference', example: '123/456', pattern: VOLUME_FOLIO }
  };

/** Label used when the jurisdiction is unknown. */
export const DEFAULT_TITLE_REFERENCE_LABEL = 'Title Reference';
/** Placeholder used when the jurisdiction is unknown. */
const DEFAULT_TITLE_REFERENCE_PLACEHOLDER = 'Add title reference';

const AU_STATE_CODES = new Set<string>(AUSTRALIAN_STATES.map((s) => s.value));

/** Normalise an arbitrary state string to a known AU code, or `undefined`. */
export function normaliseAuState(raw?: string | null): AuStateCode | undefined {
  if (typeof raw !== 'string') return undefined;
  const code = raw.trim().toUpperCase();
  return AU_STATE_CODES.has(code) ? (code as AuStateCode) : undefined;
}

/**
 * Jurisdictions where title search (folio/combo order, Verify, Locator) is
 * **temporarily switched off in the UI** (LF-4158 / LF-4258 handover §5).
 * This is a FE-only limitation — there is no BE flag. VIC was lifted in
 * LF-5459 (folio search only; the BE still hard-rejects a VIC Locator with
 * `422 UNSUPPORTED_JURISDICTION`, since D&D's Locator itself doesn't cover
 * VIC — see `locate.service.ts`).
 */
export const TITLE_SEARCH_UNSUPPORTED_STATES = new Set<AuStateCode>([]);

/**
 * `true` when `state` normalises to a jurisdiction where title search is
 * **currently unavailable** (see `TITLE_SEARCH_UNSUPPORTED_STATES`). Single
 * source of truth for gating every title-search surface — the Order drawer,
 * Verify/Locator affordances, the Start Discovery wizard, and the discovery
 * "Orders" bucket all consult this.
 *
 * A missing / unknown state returns `false` (NOT unsupported): such items are
 * still orderable once the lawyer supplies the state (the Order drawer's
 * missing-state path), so they must not be silently dropped — only an
 * explicit unsupported jurisdiction is excluded.
 */
export function isTitleSearchUnsupportedState(state?: string | null): boolean {
  const code = normaliseAuState(state);
  return code ? TITLE_SEARCH_UNSUPPORTED_STATES.has(code) : false;
}

/**
 * Resolve the jurisdiction that drives the title-reference field — the
 * property's own state first, falling back to the law firm's state when the
 * property has none (LF-4071: "primarily based on the property, secondarily on
 * the law firm").
 */
export function resolveTitleReferenceState(
  propertyState?: string | null,
  lawFirmState?: string | null
): AuStateCode | undefined {
  return normaliseAuState(propertyState) ?? normaliseAuState(lawFirmState);
}

export function getTitleReferenceConfig(
  state?: string | null
): TitleReferenceConfig | undefined {
  const code = normaliseAuState(state);
  return code ? TITLE_REFERENCE_CONFIG[code] : undefined;
}

export function getTitleReferenceLabel(state?: string | null): string {
  return getTitleReferenceConfig(state)?.label ?? DEFAULT_TITLE_REFERENCE_LABEL;
}

export function getTitleReferencePlaceholder(state?: string | null): string {
  const config = getTitleReferenceConfig(state);
  return config
    ? `e.g. ${config.example}`
    : DEFAULT_TITLE_REFERENCE_PLACEHOLDER;
}

/**
 * The message for a reference we can see is wrong but whose jurisdiction we
 * do not know, so cannot give an example for.
 */
const GENERIC_TITLE_REFERENCE_MESSAGE =
  'Enter a valid title reference: letters and numbers, joined by / or -, with no spaces (e.g. 18/218470).';

function getTitleReferenceMessage(state: AuStateCode): string {
  const config = TITLE_REFERENCE_CONFIG[state];
  return `Enter a valid ${config.label} for ${state} (e.g. ${config.example})`;
}

/**
 * Validate a title reference against a jurisdiction's expected format.
 *
 * Returns an error message string when the value is present, the jurisdiction
 * is known, and the value doesn't match the (lenient) pattern. Returns `null`
 * — i.e. valid — for an empty value (the field is optional) or an unknown
 * jurisdiction (we can't infer a format, so we stay out of the way).
 */
export function validateTitleReference(
  value?: string | null,
  state?: string | null
): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  const code = normaliseAuState(state);
  if (code) {
    return TITLE_REFERENCE_CONFIG[code].pattern.test(trimmed)
      ? null
      : getTitleReferenceMessage(code);
  }

  /*
    **An unknown jurisdiction still rejects free text** (VALE-1476).

    This used to return `null` — "no state, no opinion" — and that was the
    hole: an AI-extracted property routinely arrives with no state of its own,
    so nothing anywhere checked the reference and `Lot 18 DP 218470` was saved
    in silence. Which jurisdiction it is decides the LABEL and the example in
    the message; it does not decide whether a phrase with spaces in it is a
    title reference, because in none of them is it one.

    `COMPACT_ID` is the floor deliberately — the most permissive of the four
    patterns, so every jurisdiction's valid shape passes it and only the
    descriptive free text the extraction sometimes proposes is caught. A value
    that clears this and would fail its own jurisdiction's stricter pattern is
    still accepted here, which is the right trade: guessing a jurisdiction we
    were not told is how you refuse a reference that is actually fine.
  */
  return COMPACT_ID.test(trimmed) ? null : GENERIC_TITLE_REFERENCE_MESSAGE;
}
